import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  SwapHoriz as SwapHorizIcon,
  CameraAlt as CameraIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import Layout from './Layout';
import ContainerPhotosModal from './ContainerPhotosModal';
import { containersAPI, nominationsAPI } from '../services/api';
import type { ContainerPhotoItem } from '../shared/types/containerPhoto';

const AVG_GROSS_OPTIONS = [25, 50, 1000] as const;
const TARE_WEIGHT_PRESETS = [80, 150] as const;

const TARE_GR_TO_KG = 1000;

/** Total Net (kg) = Total Gross (kg) − (Total Tare (gr) / 1000) */
function getDisplayTotalNetKg(grossKg: number | null | undefined, tareGr: number | null | undefined): number | null {
  if (grossKg == null || tareGr == null || Number.isNaN(Number(grossKg)) || Number.isNaN(Number(tareGr))) return null;
  const tareKg = Number(tareGr) / TARE_GR_TO_KG;
  return Math.max(0, Number(grossKg) - tareKg);
}

interface ContainerItem {
  containerId: number;
  nominationId: number;
  number: string;
  agencySeal: string;
  batch_number?: string;
  bagsNumber: number;
  avgGrossWeightPerBag: number;
  tareWeightPerBag: number;
  totalGrossWeight: number;
  totalTareWeight: number;
  totalNetWeight: number;
  isEnded?: boolean | null;
  createdAt: string;
  updatedAt: string;
  /** URL to load seal image */
  sealPhoto?: string | null;
  /** Seal photo filename (without seal_photo/ prefix) for display */
  sealPhotoName?: string | null;
  /** Array of URLs to load container images from archive */
  containerPhoto?: string[] | null;
  /** Photos by category from API */
  photos?: ContainerPhotoItem[];
  changeReason?: string | null;
  nomination?: {
    nominationId: number;
    vesselName: string;
    loadingPort: string;
    dischargePort: string;
    nominationName?: string;
  };
}

const Containers: React.FC = () => {
  const [containers, setContainers] = useState<ContainerItem[]>([]);
  const [filteredContainers, setFilteredContainers] = useState<ContainerItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingContainer, setEditingContainer] = useState<ContainerItem | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSuccess, setEditSuccess] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingContainer, setDeletingContainer] = useState<ContainerItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [availableNominations, setAvailableNominations] = useState<Array<{ nominationId: number; nominationName?: string; vesselName: string; loadingPort?: string; dischargePort?: string }>>([]);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [selectedContainer, setSelectedContainer] = useState<ContainerItem | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [changeNominationModalOpen, setChangeNominationModalOpen] = useState(false);
  const [changeNominationNominationId, setChangeNominationNominationId] = useState<number>(0);
  const [changeReason, setChangeReason] = useState('');
  const [isSavingChangeNomination, setIsSavingChangeNomination] = useState(false);
  const [changeNominationError, setChangeNominationError] = useState<string | null>(null);
  const [changeReasonModalOpen, setChangeReasonModalOpen] = useState(false);
  const [changeReasonModalText, setChangeReasonModalText] = useState('');

  // Form state for editing (subset of fields)
  const [formData, setFormData] = useState({
    number: '',
    agencySeal: '',
    batch_number: '',
    bagsNumber: 0,
    avgGrossWeightPerBag: 0 as number | null,
    tareWeightPerBag: 0,
    isEnded: false,
  });
  const [tareWeightPreset, setTareWeightPreset] = useState<80 | 150 | 'custom' | ''>('custom');
  const [avgGrossPreset, setAvgGrossPreset] = useState<25 | 50 | 1000 | 'custom'>('custom');

  const [createFormData, setCreateFormData] = useState({
    nominationId: 0,
    number: '',
    agencySeal: '',
    batch_number: '',
    bagsNumber: 0,
    avgGrossWeightPerBag: null as number | null,
    tareWeightPerBag: 0,
  });
  const [createAvgGrossPreset, setCreateAvgGrossPreset] = useState<25 | 50 | 1000 | 'custom' | ''>('');
  const [createTareWeightPreset, setCreateTareWeightPreset] = useState<80 | 150 | 'custom' | ''>('');

  const computeDerived = () => {
    const avg = formData.avgGrossWeightPerBag != null ? Number(formData.avgGrossWeightPerBag) : 0;
    const totalGrossWeight = Number(formData.bagsNumber) * avg;
    const totalTareWeightGr = Number(formData.bagsNumber) * Number(formData.tareWeightPerBag);
    const totalNetWeight = getDisplayTotalNetKg(totalGrossWeight, totalTareWeightGr) ?? 0;
    return { totalGrossWeight, totalTareWeight: totalTareWeightGr, totalNetWeight };
  };

  useEffect(() => {
    fetchContainers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadNominations = async () => {
      try {
        const resp = await nominationsAPI.getAll();
        if (resp.data?.data) {
          setAvailableNominations(resp.data.data.map((w: any) => ({
            nominationId: w.nominationId,
            nominationName: w.nominationName || w.vesselName,
            vesselName: w.vesselName,
            loadingPort: w.loadingPort,
            dischargePort: w.dischargePort,
          })));
        }
      } catch (e) {
        setAvailableNominations([]);
      }
    };
    loadNominations();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredContainers(containers);
    } else {
      const filtered = containers.filter(container =>
        container.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        container.agencySeal.toLowerCase().includes(searchTerm.toLowerCase()) ||
        container.nomination?.vesselName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        container.nomination?.loadingPort.toLowerCase().includes(searchTerm.toLowerCase()) ||
        container.nomination?.dischargePort.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredContainers(filtered);
    }
  }, [searchTerm, containers]);

  const fetchContainers = async () => {
    if (isFetching) {
      return;
    }
    
    try {
      setIsFetching(true);
      setIsLoading(true);
      // Clear existing data before fetching
      setContainers([]);
      setFilteredContainers([]);
      
      const response = await containersAPI.getAll();
      if (response.data.status === 'success') {
        const baseList: ContainerItem[] = response.data.data;
        setContainers(baseList);
        setFilteredContainers(baseList);
      } else {
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
  };

  const handleEditContainer = (containerId: number) => {
    const container = containers.find(c => c.containerId === containerId);
    if (!container) return;
    setEditingContainer(container);
    const tare = Number(container.tareWeightPerBag) || 0;
    const tarePreset: 80 | 150 | 'custom' | '' = tare === 80 ? 80 : tare === 150 ? 150 : tare === 0 ? '' : 'custom';
    setTareWeightPreset(tarePreset);
    const avg = container.avgGrossWeightPerBag != null ? Number(container.avgGrossWeightPerBag) : null;
    const avgPreset: 25 | 50 | 1000 | 'custom' = avg === 25 ? 25 : avg === 50 ? 50 : avg === 1000 ? 1000 : 'custom';
    setAvgGrossPreset(avgPreset);
    setFormData({
      number: container.number,
      agencySeal: container.agencySeal,
      batch_number: container.batch_number ?? '',
      bagsNumber: container.bagsNumber,
      avgGrossWeightPerBag: avg ?? 0,
      tareWeightPerBag: tare,
      isEnded: container.isEnded ?? false,
    });
    setEditError(null);
    setEditSuccess(false);
    setEditDialogOpen(true);
  };

  const handleDeleteContainer = (containerId: number) => {
    const container = containers.find(c => c.containerId === containerId);
    if (!container) return;
    setDeletingContainer(container);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleViewPhotos = (container: ContainerItem) => {
    setSelectedContainer(container);
    setPhotoModalOpen(true);
  };

  const handleClosePhotoModal = () => {
    setPhotoModalOpen(false);
    setSelectedContainer(null);
  };

  const handleAddContainer = () => {
    setCreateFormData({
      nominationId: 0,
      number: '',
      agencySeal: '',
      batch_number: '',
      bagsNumber: 0,
      avgGrossWeightPerBag: null,
      tareWeightPerBag: 0,
    });
    setCreateTareWeightPreset('');
    setCreateAvgGrossPreset('');
    setCreateError(null);
    setCreateSuccess(false);
    setCreateDialogOpen(true);
  };

  /** Same format as Nominations (toLocaleString), split into date and time for two-line display */
  const formatDateTimeParts = (dateString: string): { date: string; time: string } => {
    const full = new Date(dateString).toLocaleString();
    const sep = full.includes(', ') ? ', ' : ' ';
    const parts = full.split(sep);
    if (parts.length >= 2) return { date: parts[0], time: parts.slice(1).join(sep) };
    return { date: full, time: '' };
  };

  const detectImageFormat = (base64: string): string => {
    try {
      const decoded = atob(base64.substring(0, 20));
      const bytes = new Uint8Array(decoded.length);
      for (let i = 0; i < decoded.length; i++) {
        bytes[i] = decoded.charCodeAt(i);
      }
      
      // Check for common image format signatures
      if (bytes[0] === 0xFF && bytes[1] === 0xD8) return 'image/jpeg';
      if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) return 'image/png';
      if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) return 'image/gif';
      if (bytes[0] === 0x42 && bytes[1] === 0x4D) return 'image/bmp';
      
      return 'image/jpeg'; // default
    } catch (e) {
      return 'image/jpeg'; // default
    }
  };

  const handleFormChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateFormChange = (field: keyof typeof createFormData, value: any) => {
    setCreateFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingContainer(null);
    setEditError(null);
    setEditSuccess(false);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setCreateError(null);
    setCreateSuccess(false);
  };

  const handleCreateContainer = async () => {
    try {
      setIsCreating(true);
      setCreateError(null);

      if (!createFormData.nominationId) {
        setCreateError('Nomination is required');
        return;
      }
      if (!createFormData.number.trim()) {
        setCreateError('Container Number is required');
        return;
      }

      const bagsNumber = Math.max(0, Number(createFormData.bagsNumber) || 0);
      const avgGross = createFormData.avgGrossWeightPerBag != null ? Number(createFormData.avgGrossWeightPerBag) : 0;
      const tareWeightPerBag = Math.max(0, Number(createFormData.tareWeightPerBag) || 0);
      const totalGrossWeight = bagsNumber * (avgGross >= 0 ? avgGross : 0); // kg
      const totalTareWeightGr = bagsNumber * tareWeightPerBag; // gr
      const totalNetWeight = getDisplayTotalNetKg(totalGrossWeight, totalTareWeightGr) ?? 0;

      const payload = {
        nominationId: createFormData.nominationId,
        number: createFormData.number.trim(),
        agencySeal: createFormData.agencySeal?.trim() ?? '',
        batch_number: createFormData.batch_number?.trim() ?? '',
        bagsNumber,
        avgGrossWeightPerBag: createFormData.avgGrossWeightPerBag ?? undefined,
        tareWeightPerBag,
        totalGrossWeight,
        totalTareWeight: totalTareWeightGr,
        totalNetWeight,
      };

      const resp = await containersAPI.create(payload);
      if (resp.data.status === 'success') {
        setCreateSuccess(true);
        await fetchContainers();
        setTimeout(() => {
          handleCloseCreateDialog();
        }, 800);
      }
    } catch (error: any) {
      setCreateError(error.response?.data?.message || 'Failed to create container');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeletingContainer(null);
    setDeleteError(null);
  };

  const handleOpenChangeReason = (text: string) => {
    setChangeReasonModalText(text);
    setChangeReasonModalOpen(true);
  };

  const handleCloseChangeReasonModal = () => {
    setChangeReasonModalOpen(false);
    setChangeReasonModalText('');
  };

  const handleUpdateContainer = async () => {
    if (!editingContainer) return;
    try {
      setIsUpdating(true);
      setEditError(null);

      // basic validation
      if (!formData.number.trim() || !formData.agencySeal.trim()) {
        setEditError('Number and Agency Seal are required');
        return;
      }
      if (formData.bagsNumber <= 0) {
        setEditError('Bags number must be positive');
        return;
      }

      const payload = {
        number: formData.number,
        agencySeal: formData.agencySeal,
        batch_number: formData.batch_number?.trim() ?? '',
        bagsNumber: formData.bagsNumber,
        avgGrossWeightPerBag: formData.avgGrossWeightPerBag ?? undefined,
        tareWeightPerBag: formData.tareWeightPerBag,
        isEnded: formData.isEnded,
      };

      const resp = await containersAPI.update(editingContainer.containerId, payload);
      if (resp.data.status === 'success') {
        setEditSuccess(true);
        // Refresh list to reflect updatedAt
        await fetchContainers();
        setTimeout(() => {
          handleCloseEditDialog();
        }, 800);
      }
    } catch (error: any) {
      setEditError(error.response?.data?.message || 'Failed to update container');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingContainer) return;
    try {
      setIsDeleting(true);
      setDeleteError(null);
      await containersAPI.remove(deletingContainer.containerId);
      await fetchContainers();
      setDeleteDialogOpen(false);
      setDeletingContainer(null);
    } catch (error: any) {
      setDeleteError(error.response?.data?.message || 'Failed to delete container');
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleSelectContainer = (containerId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(containerId)) next.delete(containerId);
      else next.add(containerId);
      return next;
    });
  };

  const openChangeNominationModal = () => {
    setChangeNominationNominationId(availableNominations[0]?.nominationId ?? 0);
    setChangeReason('');
    setChangeNominationError(null);
    setSelectedIds(new Set());
    setChangeNominationModalOpen(true);
  };

  const toggleSelectAllInModal = () => {
    if (selectedIds.size === filteredContainers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredContainers.map((c) => c.containerId)));
    }
  };

  const closeChangeNominationModal = () => {
    setChangeNominationModalOpen(false);
    setChangeNominationError(null);
  };

  const handleSaveChangeNomination = async () => {
    if (selectedIds.size === 0) {
      setChangeNominationError('Select at least one container');
      return;
    }
    if (!changeNominationNominationId) {
      setChangeNominationError('Select a nomination');
      return;
    }
    try {
      setIsSavingChangeNomination(true);
      setChangeNominationError(null);
      const payload = Array.from(selectedIds).map((containerId) => ({
        containerId,
        nominationId: changeNominationNominationId,
        changeReason: changeReason.trim() || undefined,
      }));
      await containersAPI.bulkUpdate(payload);
      setSelectedIds(new Set());
      await fetchContainers();
      closeChangeNominationModal();
    } catch (error: any) {
      setChangeNominationError(error.response?.data?.message || 'Failed to update nominations');
    } finally {
      setIsSavingChangeNomination(false);
    }
  };


  return (
    <Layout>
      <Container maxWidth={false} sx={{ mt: 2, mb: 4, px: { xs: 1.5, sm: 2, md: 3 } }}>
        {/* Page Title */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            flexWrap="wrap"
            gap={2}
          >
            <Typography variant="h4" component="h1">
              Containers Management
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="body1" color="text.secondary">
                Total Containers: {containers.length}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<SwapHorizIcon />}
                onClick={openChangeNominationModal}
                sx={{ borderColor: '#CA4300', color: '#CA4300', '&:hover': { borderColor: '#A03600', backgroundColor: 'rgba(202,67,0,0.08)' } }}
              >
                Change nomination
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddContainer}
                sx={{
                  backgroundColor: '#CA4300',
                  '&:hover': {
                    backgroundColor: '#A03600'
                  }
                }}
              >
                Add Container
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Search */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              label="Search Containers"
              placeholder="Search by container number, agency seal, vessel name, or ports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              Showing {filteredContainers.length} of {containers.length} containers
            </Typography>
          </Box>
        </Paper>

        {/* Containers Table */}
        <Paper>
          <TableContainer sx={{ 
            overflowX: 'auto',
            maxWidth: '100%',
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#CA4300',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#A03600',
            }
          }}>
            <Table size="small" sx={{ 
              tableLayout: 'auto',
              minWidth: 1200,
              width: '100%',
              '& .MuiTableHead-root': {
                '& .MuiTableRow-root': {
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                },
              },
            }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Container Number</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Agency Seal</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Batch Number</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Nomination</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Bags</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Is ended</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Avg Gross/Bag</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Tare/Bag</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Total Gross</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Total Tare</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Total Net</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Updated At</TableCell>
                  <TableCell align="left" sx={{ whiteSpace: 'nowrap' }}>Changes</TableCell>
                  <TableCell align="left" sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={14} align="center">
                      Loading containers...
                    </TableCell>
                  </TableRow>
                ) : filteredContainers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} align="center">
                      No containers found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredContainers.map((container) => (
                    <TableRow key={container.containerId} hover>
                      <TableCell>
                        <Chip 
                          label={container.number} 
                          sx={{ 
                            backgroundColor: '#CA4300', 
                            color: '#ffffff',
                            fontWeight: 'bold'
                          }} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography 
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '90px'
                          }}
                        >
                          {container.agencySeal}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100px',
                          }}
                        >
                          {container.batch_number || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {container.nomination ? (
                        <Box>
                            <Typography 
                              variant="body2" 
                              fontWeight="medium"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '140px'
                              }}
                            >
                              {container.nomination.vesselName}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#49738b',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '140px',
                                display: 'block'
                              }}
                            >
                              {container.nomination.loadingPort} → {container.nomination.dischargePort}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No Nomination
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{container.bagsNumber.toLocaleString()}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={container.isEnded ? 'Yes' : 'No'}
                          size="small"
                          color={container.isEnded ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {container.avgGrossWeightPerBag != null ? `${Number(container.avgGrossWeightPerBag).toFixed(2)} kg` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{container.tareWeightPerBag.toFixed(2)} gr</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{container.totalGrossWeight != null && !Number.isNaN(Number(container.totalGrossWeight)) ? Number(container.totalGrossWeight).toLocaleString() : '—'} kg</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{container.totalTareWeight != null && !Number.isNaN(Number(container.totalTareWeight)) ? Number(container.totalTareWeight).toLocaleString() : '—'} gr</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{(() => { const n = getDisplayTotalNetKg(container.totalGrossWeight, container.totalTareWeight); return n != null ? n.toLocaleString() : '—'; })()} kg</Typography>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const { date, time } = formatDateTimeParts(container.updatedAt);
                          return (
                            <>
                              <Typography variant="body2" component="span" display="block">
                                {date}
                              </Typography>
                              {time ? (
                                <Typography variant="body2" component="span" display="block">
                                  {time}
                                </Typography>
                              ) : null}
                            </>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {container.changeReason ? (
                          <IconButton
                            size="small"
                            onClick={() => handleOpenChangeReason(container.changeReason!)}
                            sx={{ color: '#1976d2' }}
                          >
                            <InfoIcon />
                          </IconButton>
                        ) : (
                          <Tooltip title="No information">
                            <span>
                              <IconButton size="small" disabled sx={{ color: undefined }}>
                                <InfoIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Tooltip title="View Photos">
                            <IconButton
                              size="small"
                              onClick={() => handleViewPhotos(container)}
                              sx={{ color: '#56c785' }}
                            >
                              <CameraIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEditContainer(container.containerId)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteContainer(container.containerId)}
                              sx={{ color: '#b91c1c' }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Edit Container Dialog */}
        <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
          <DialogTitle>Edit Container</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {editError && <Alert severity="error">{editError}</Alert>}
              {editSuccess && <Alert severity="success">Container updated successfully</Alert>}

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Container Number"
                  value={formData.number}
                  onChange={(e) => handleFormChange('number', e.target.value)}
                  required
                />
                <TextField
                  fullWidth
                  label="Agency Seal"
                  value={formData.agencySeal}
                  onChange={(e) => handleFormChange('agencySeal', e.target.value)}
                  required
                />
              </Box>
              <TextField
                fullWidth
                label="Batch Number"
                value={formData.batch_number}
                onChange={(e) => handleFormChange('batch_number', e.target.value)}
              />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  checked={formData.isEnded}
                  onChange={(e) => handleFormChange('isEnded', e.target.checked)}
                  color="primary"
                />
                <Typography variant="body2">Is ended</Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Bags Number"
                  value={formData.bagsNumber}
                  onChange={(e) => handleFormChange('bagsNumber', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 1 }}
                  required
                />
                <FormControl fullWidth>
                  <InputLabel>Avg Gross/Bag (kg)</InputLabel>
                  <Select
                    value={typeof avgGrossPreset === 'number' ? String(avgGrossPreset) : formData.avgGrossWeightPerBag == null ? (avgGrossPreset === 'custom' ? 'custom' : '') : 'custom'}
                    label="Avg Gross/Bag (kg)"
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '25' || v === '50' || v === '1000') {
                        const n = Number(v) as 25 | 50 | 1000;
                        setAvgGrossPreset(n);
                        handleFormChange('avgGrossWeightPerBag', n);
                      } else if (v === '') {
                        setAvgGrossPreset('custom');
                        handleFormChange('avgGrossWeightPerBag', null);
                      } else {
                        setAvgGrossPreset('custom');
                      }
                    }}
                  >
                    <MenuItem value="">—</MenuItem>
                    {AVG_GROSS_OPTIONS.map((v) => (
                      <MenuItem key={v} value={String(v)}>{v}</MenuItem>
                    ))}
                    <MenuItem value="custom">Custom (enter below)</MenuItem>
                  </Select>
                </FormControl>
                {(avgGrossPreset === 'custom' || (formData.avgGrossWeightPerBag != null && !AVG_GROSS_OPTIONS.includes(formData.avgGrossWeightPerBag as 25 | 50 | 1000))) && (
                  <TextField
                    fullWidth
                    type="number"
                    label="Avg Gross/Bag (kg) — manual"
                    value={formData.avgGrossWeightPerBag ?? ''}
                    onChange={(e) => handleFormChange('avgGrossWeightPerBag', parseFloat(e.target.value) || null)}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                )}
              </Box>
              <FormControl fullWidth required>
                <InputLabel>Tare Weight/Bag (gr)</InputLabel>
                <Select
                  value={tareWeightPreset === 'custom' ? 'custom' : tareWeightPreset === '' ? '' : String(tareWeightPreset)}
                  label="Tare Weight/Bag (gr)"
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '80' || v === '150') {
                      const n = (v === '80' ? 80 : 150) as 80 | 150;
                      setTareWeightPreset(n);
                      handleFormChange('tareWeightPerBag', n);
                    } else if (v === '') {
                      setTareWeightPreset('');
                      handleFormChange('tareWeightPerBag', 0);
                    } else {
                      setTareWeightPreset('custom');
                    }
                  }}
                >
                  <MenuItem value="">—</MenuItem>
                  {TARE_WEIGHT_PRESETS.map((v) => (
                    <MenuItem key={v} value={String(v)}>{v}</MenuItem>
                  ))}
                  <MenuItem value="custom">Custom value (enter below)</MenuItem>
                </Select>
              </FormControl>
              {tareWeightPreset === 'custom' && (
                <TextField
                  fullWidth
                  type="number"
                  label="Tare Weight/Bag (gr) — manual"
                  value={formData.tareWeightPerBag}
                  onChange={(e) => handleFormChange('tareWeightPerBag', parseInt(e.target.value, 10) || 0)}
                  inputProps={{ min: 0, step: 1 }}
                />
              )}

              {/* Derived (read-only) totals */}
              {(() => {
                const d = computeDerived();
                return (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Total Gross (kg)"
                      value={d.totalGrossWeight.toLocaleString()}
                      InputProps={{ readOnly: true }}
                    />
                    <TextField
                      fullWidth
                      label="Total Tare (gr)"
                      value={d.totalTareWeight.toLocaleString()}
                      InputProps={{ readOnly: true }}
                    />
                    <TextField
                      fullWidth
                      label="Total Net (kg)"
                      value={d.totalNetWeight != null && !Number.isNaN(Number(d.totalNetWeight)) ? Number(d.totalNetWeight).toLocaleString() : '—'}
                      InputProps={{ readOnly: true }}
                    />
                  </Box>
                );
              })()}

            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={handleCloseEditDialog}
              disabled={isUpdating}
              sx={{
                borderColor: '#CA4300',
                color: '#CA4300',
                borderRadius: 1,
                boxShadow: 2,
                '&:hover': {
                  borderColor: '#A03600',
                  backgroundColor: 'rgba(202, 67, 0, 0.08)',
                  boxShadow: 2,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateContainer}
              variant="contained"
              disabled={isUpdating}
              sx={{
                backgroundColor: '#CA4300',
                '&:hover': { backgroundColor: '#A03600' }
              }}
            >
              {isUpdating ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Container Dialog */}
        <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="md" fullWidth>
          <DialogTitle>Add Container</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {createError && <Alert severity="error">{createError}</Alert>}
              {createSuccess && <Alert severity="success">Container created successfully</Alert>}

              <FormControl fullWidth required>
                <InputLabel>Nomination</InputLabel>
                <Select
                  value={createFormData.nominationId}
                  label="Nomination"
                  onChange={(e) => handleCreateFormChange('nominationId', e.target.value)}
                >
                  {availableNominations.map(w => (
                    <MenuItem key={w.nominationId} value={w.nominationId}>
                      {w.vesselName} {w.loadingPort && w.dischargePort ? `(${w.loadingPort} → ${w.dischargePort})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Container Number"
                  value={createFormData.number}
                  onChange={(e) => handleCreateFormChange('number', e.target.value)}
                  required
                />
                <TextField
                  fullWidth
                  label="Agency Seal"
                  value={createFormData.agencySeal}
                  onChange={(e) => handleCreateFormChange('agencySeal', e.target.value)}
                />
              </Box>
              <TextField
                fullWidth
                label="Batch Number"
                value={createFormData.batch_number}
                onChange={(e) => handleCreateFormChange('batch_number', e.target.value)}
              />

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Bags Number"
                  value={createFormData.bagsNumber}
                  onChange={(e) => handleCreateFormChange('bagsNumber', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                />
                <FormControl fullWidth>
                  <InputLabel>Avg Gross/Bag (kg)</InputLabel>
                  <Select
                    value={typeof createAvgGrossPreset === 'number' ? String(createAvgGrossPreset) : createAvgGrossPreset === 'custom' ? 'custom' : ''}
                    label="Avg Gross/Bag (kg)"
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '25' || v === '50' || v === '1000') {
                        const n = Number(v) as 25 | 50 | 1000;
                        setCreateAvgGrossPreset(n);
                        handleCreateFormChange('avgGrossWeightPerBag', n);
                      } else if (v === '') {
                        setCreateAvgGrossPreset('');
                        handleCreateFormChange('avgGrossWeightPerBag', null);
                      } else {
                        setCreateAvgGrossPreset('custom');
                      }
                    }}
                  >
                    <MenuItem value="">—</MenuItem>
                    {AVG_GROSS_OPTIONS.map((v) => (
                      <MenuItem key={v} value={String(v)}>{v}</MenuItem>
                    ))}
                    <MenuItem value="custom">Custom (enter below)</MenuItem>
                  </Select>
                </FormControl>
                {createAvgGrossPreset === 'custom' && (
                  <TextField
                    fullWidth
                    type="number"
                    label="Avg Gross/Bag (kg) — manual"
                    value={createFormData.avgGrossWeightPerBag ?? ''}
                    onChange={(e) => handleCreateFormChange('avgGrossWeightPerBag', parseFloat(e.target.value) || null)}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                )}
              </Box>

              <FormControl fullWidth>
                <InputLabel>Tare Weight/Bag (gr)</InputLabel>
                <Select
                  value={createTareWeightPreset === 'custom' ? 'custom' : createTareWeightPreset === '' ? '' : String(createTareWeightPreset)}
                  label="Tare Weight/Bag (gr)"
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '80' || v === '150') {
                      const n = (v === '80' ? 80 : 150) as 80 | 150;
                      setCreateTareWeightPreset(n);
                      handleCreateFormChange('tareWeightPerBag', n);
                    } else if (v === '') {
                      setCreateTareWeightPreset('');
                      handleCreateFormChange('tareWeightPerBag', 0);
                    } else {
                      setCreateTareWeightPreset('custom');
                    }
                  }}
                >
                  <MenuItem value="">—</MenuItem>
                  {TARE_WEIGHT_PRESETS.map((v) => (
                    <MenuItem key={v} value={String(v)}>{v}</MenuItem>
                  ))}
                  <MenuItem value="custom">Custom value (enter below)</MenuItem>
                </Select>
              </FormControl>
              {createTareWeightPreset === 'custom' && (
                <TextField
                  fullWidth
                  type="number"
                  label="Tare Weight/Bag (gr) — manual"
                  value={createFormData.tareWeightPerBag}
                  onChange={(e) => handleCreateFormChange('tareWeightPerBag', parseInt(e.target.value, 10) || 0)}
                  inputProps={{ min: 0, step: 1 }}
                />
              )}

              {(() => {
                const avgGross = createFormData.avgGrossWeightPerBag != null ? Number(createFormData.avgGrossWeightPerBag) : 0;
                const totalGrossKg = Number(createFormData.bagsNumber) * avgGross;
                const totalTareGr = Number(createFormData.bagsNumber) * Number(createFormData.tareWeightPerBag);
                const totalNetKg = getDisplayTotalNetKg(totalGrossKg, totalTareGr) ?? 0;
                return (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                    <TextField fullWidth label="Total Gross (kg)" value={totalGrossKg.toLocaleString()} InputProps={{ readOnly: true }} />
                    <TextField fullWidth label="Total Tare (gr)" value={totalTareGr.toLocaleString()} InputProps={{ readOnly: true }} />
                    <TextField fullWidth label="Total Net (kg)" value={totalNetKg.toLocaleString()} InputProps={{ readOnly: true }} />
                  </Box>
                );
              })()}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={handleCloseCreateDialog}
              disabled={isCreating}
              sx={{
                borderColor: '#CA4300',
                color: '#CA4300',
                borderRadius: 1,
                boxShadow: 2,
                '&:hover': {
                  borderColor: '#A03600',
                  backgroundColor: 'rgba(202, 67, 0, 0.08)',
                  boxShadow: 2,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateContainer}
              variant="contained"
              disabled={isCreating}
              sx={{ backgroundColor: '#CA4300', '&:hover': { backgroundColor: '#A03600' } }}
            >
              {isCreating ? 'Creating...' : 'Create Container'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Change nomination modal */}
        <Dialog open={changeNominationModalOpen} onClose={closeChangeNominationModal} maxWidth="sm" fullWidth>
          <DialogTitle>Change nomination</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {changeNominationError && <Alert severity="error">{changeNominationError}</Alert>}
              <FormControl fullWidth required>
                <InputLabel>New nomination</InputLabel>
                <Select
                  value={changeNominationNominationId}
                  label="New nomination"
                  onChange={(e) => setChangeNominationNominationId(Number(e.target.value))}
                >
                  {availableNominations.map((n) => (
                    <MenuItem key={n.nominationId} value={n.nominationId}>
                      {n.nominationName || n.vesselName}
                      {n.loadingPort && n.dischargePort ? ` (${n.loadingPort} → ${n.dischargePort})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Reason for change"
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                multiline
                rows={2}
                placeholder="Optional: describe why nomination was changed"
              />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                Select containers ({selectedIds.size} selected)
              </Typography>
              <TableContainer sx={{ maxHeight: 220, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ backgroundColor: 'background.paper' }}>
                        <Checkbox
                          indeterminate={selectedIds.size > 0 && selectedIds.size < filteredContainers.length}
                          checked={filteredContainers.length > 0 && selectedIds.size === filteredContainers.length}
                          onChange={toggleSelectAllInModal}
                        />
                      </TableCell>
                      <TableCell sx={{ backgroundColor: 'background.paper' }}>Container</TableCell>
                      <TableCell sx={{ backgroundColor: 'background.paper' }}>Current nomination</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredContainers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          No containers
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredContainers.map((c) => (
                        <TableRow key={c.containerId} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedIds.has(c.containerId)}
                              onChange={() => toggleSelectContainer(c.containerId)}
                            />
                          </TableCell>
                          <TableCell>{c.number}</TableCell>
                          <TableCell>
                            {c.nomination?.vesselName ?? c.nomination?.nominationName ?? '—'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={closeChangeNominationModal}
              disabled={isSavingChangeNomination}
              sx={{
                borderColor: '#CA4300',
                color: '#CA4300',
                borderRadius: 1,
                boxShadow: 2,
                '&:hover': {
                  borderColor: '#A03600',
                  backgroundColor: 'rgba(202, 67, 0, 0.08)',
                  boxShadow: 2,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveChangeNomination}
              variant="contained"
              disabled={isSavingChangeNomination || !changeNominationNominationId || selectedIds.size === 0}
              sx={{ backgroundColor: '#CA4300', '&:hover': { backgroundColor: '#A03600' } }}
            >
              {isSavingChangeNomination ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Container Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Delete Container</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {deleteError && <Alert severity="error">{deleteError}</Alert>}
              <Typography variant="body1">
                Are you sure you want to delete the container "{deletingContainer?.number}"?
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={handleCloseDeleteDialog}
              disabled={isDeleting}
              sx={{
                borderColor: '#CA4300',
                color: '#CA4300',
                borderRadius: 1,
                boxShadow: 2,
                '&:hover': {
                  borderColor: '#A03600',
                  backgroundColor: 'rgba(202, 67, 0, 0.08)',
                  boxShadow: 2,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              disabled={isDeleting}
              sx={{ backgroundColor: '#b91c1c', '&:hover': { backgroundColor: '#991b1b' } }}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Change Reason Modal */}
        <Dialog open={changeReasonModalOpen} onClose={handleCloseChangeReasonModal} maxWidth="sm" fullWidth>
          <DialogTitle>Change Reason</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {changeReasonModalText || '—'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={handleCloseChangeReasonModal}
              sx={{
                borderColor: '#CA4300',
                color: '#CA4300',
                borderRadius: 1,
                boxShadow: 2,
                '&:hover': { borderColor: '#A03600', backgroundColor: 'rgba(202, 67, 0, 0.08)', boxShadow: 2 },
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        <ContainerPhotosModal
          open={photoModalOpen}
          onClose={handleClosePhotoModal}
          containerId={selectedContainer?.containerId ?? 0}
          containerNumber={selectedContainer?.number ?? ''}
          photos={selectedContainer?.photos}
          sealPhoto={selectedContainer?.sealPhoto}
          sealPhotoName={selectedContainer?.sealPhotoName}
        />
      </Container>
    </Layout>
  );
};

export { Containers };
export default Containers;
